import { useState, useEffect, useCallback } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

// ---- Row shapes returned by the Revenue Plays monitoring views ----
export interface PlayKpis {
  total_customers: number;
  one_time_customers: number;
  one_time_pct: number;
  superfans: number;
  active_customers: number;
  cooling_customers: number;
  at_risk_customers: number;
  lapsing_customers: number;
  lost_customers: number;
  winback_revenue_at_stake: number;
}

export interface MonthlyRevenue {
  month: string;
  orders: number;
  customers: number;
  new_customers: number;
  returning_customers: number;
  revenue: number;
  avg_order_value: number;
}

export interface SecondOrderCohort {
  cohort_month: string;
  new_customers: number;
  eligible_30d: number;
  second_within_30d: number;
  second_within_60d: number;
  pct_second_30d: number | null;
  median_days_to_second: number | null;
}

export interface WinbackTarget {
  customer_phone: string;
  customer_name: string | null;
  is_loyalty: boolean;
  recency_segment: "cooling" | "at_risk" | "lapsing";
  frequency_segment: string;
  total_orders: number;
  lifetime_spend: number;
  avg_order_value: number;
  last_order_at: string;
  days_since_last_order: number;
  top_item: string | null;
}

interface RevenuePlaysData {
  kpis: PlayKpis | null;
  monthly: MonthlyRevenue[];
  cohorts: SecondOrderCohort[];
  winback: WinbackTarget[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useRevenuePlays = (): RevenuePlaysData => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();

  const [kpis, setKpis] = useState<PlayKpis | null>(null);
  const [monthly, setMonthly] = useState<MonthlyRevenue[]>([]);
  const [cohorts, setCohorts] = useState<SecondOrderCohort[]>([]);
  const [winback, setWinback] = useState<WinbackTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      // The monitoring views aren't in the generated Database types, so query
      // through an untyped client and map results onto the interfaces above.
      const sb = supabase as unknown as SupabaseClient;

      const [kpiRes, revRes, cohortRes, winbackRes] = await Promise.all([
        sb.from("v_play_kpis").select("*").maybeSingle(),
        sb.from("v_revenue_monthly").select("*").order("month", { ascending: true }),
        sb.from("v_second_order_cohort").select("*").order("cohort_month", { ascending: true }),
        sb.from("v_winback_targets").select("*"),
      ]);

      const firstError =
        kpiRes.error || revRes.error || cohortRes.error || winbackRes.error;
      if (firstError) throw firstError;

      setKpis((kpiRes.data as PlayKpis) ?? null);
      setMonthly((revRes.data as MonthlyRevenue[]) ?? []);
      setCohorts((cohortRes.data as SecondOrderCohort[]) ?? []);
      setWinback((winbackRes.data as WinbackTarget[]) ?? []);
    } catch (err) {
      console.error("Error fetching revenue plays data:", err);
      setError(err instanceof Error ? err.message : "Failed to load revenue insights");
      toast({
        title: "Error",
        description: "Failed to load revenue insights. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { kpis, monthly, cohorts, winback, loading, error, refresh: fetchData };
};
